import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import RegisterFirst from "../../App/Auth/Steps/RegisterFirst";
import RegisterSecond from "../../App/Auth/Steps/RegisterSecond";
import RegisterLast from "../../App/Auth/Steps/RegisterLast";
import ClientRegisterSecond from "../../App/Auth/Steps/ClientRegisterSecond";
import TypeAccount from "../../Components/Auth/TypeAccount";
import { userSetCurrentRegister } from "../../Redux/Actions/UserActions";
import { auth } from "../../Services/FirebaseService";
import { LayoutAuth } from "../../Components/Auth/LayoutAuth";
import { useBrandAuthConfig } from "../../Hooks/Auth/useBrandAuthConfig";

/**
 * Inscription commune prof / client sur la même URL :
 * /register?=prestataire pour un prof (coach, prestataire),
 * /register?=client pour un client,
 * sans rôle dans l'URL, le choix se fait via le dialogue TypeAccount.
 * Un ?=CODE historique (code de parrainage) reste un flux prof.
 */
const RegisterPage = () => {
  const [user] = useAuthState(auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { designConfig } = useBrandAuthConfig();
  const currentRegister = useSelector(
    (state: any) => state?.user?.currentRegister
  );
  const currentUser = useSelector((state: any) => state.user.currentUser);
  const [currentStep, setCurrentStep] = useState(currentRegister);

  // rôle porté par l'URL : ?=client, ?=prestataire, ou un code de parrainage
  const searchValue = location.search.startsWith("?=") ? location.search.slice(2) : "";
  const isClient = searchValue === "client";
  const hasRole = isClient || searchValue === "prestataire";
  const hasSponsorshipCode = searchValue !== "" && !hasRole;
  // un client déjà en cours d'inscription (session Firebase active) reste
  // sur son flux même si le paramètre a été perdu
  const clientFlow = isClient || (!!user && !!currentUser?.isClient);
  // reprise après abandon : session Firebase active mais aucun brouillon de
  // profil (retour via la connexion), le choix client / prestataire se refait
  const hasProfileDraft = !!currentUser?.teachr || !!currentUser?.client;
  const showTypeChoice = !hasRole && !hasSponsorshipCode && (!user || !hasProfileDraft);

  // client comme prof : 3 étapes, la dernière est la page de félicitations
  const totalSteps = 3;

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      dispatch(userSetCurrentRegister(currentStep + 1));
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      dispatch(userSetCurrentRegister(currentStep - 1));
    }
  };


  const displayChildren = () => {
    if (!user) {
      return <RegisterFirst nextStep={nextStep} isClient={clientFlow} />;
    }
    if (clientFlow) {
      // Espace client : profil créé (id en base) → page de félicitations,
      // sinon étape 2 (identité, élève, notifications, offres)
      if (currentUser?.client?.id) {
        return <RegisterLast isClient />;
      }
      return <ClientRegisterSecond />;
    }
    // Si connecté mais pas de profil teachr
    if (!currentUser?.teachr?.id) {
      return <RegisterSecond nextStep={nextStep} prevStep={prevStep} />;
    }
    // Si connecté avec profil teachr complet
    return <RegisterLast />;
  };

  return (
    <LayoutAuth
      currentStep={currentRegister}
      totalSteps={totalSteps}
      rightContent={
        <div className="md:w-1/3 lg:w-2/5 w-full lg:block hidden">
          {designConfig.rightContent}
        </div>
      }
    >
      {showTypeChoice && <TypeAccount navigate={navigate} forceOpen />}
      {displayChildren()}
    </LayoutAuth>
  );
};

export default RegisterPage;
